import { useEffect, useState } from "react";
import { OptionsButton } from "./OptionsButton.tsx";
import { gameConfig } from "../../app/gameConfig.ts";
import { useGt } from "./gameText.ts";
import { useT } from "../../gamzone/i18n.ts";

const PACT_STONE = "/assets/ui/pact-stone-pin-active.webp";
export const TITLE_LOGO = "/assets/ui/othellos-logo.webp";

/** Minimum time the spinner stays up, so a fast cache hit doesn't just flash. */
const MIN_LOAD_MS = 500;
/** How long the glow holds green before the tap hint replaces the spinner. */
const SETTLE_MS = 380;

type Phase = "loading" | "settled" | "ready";

/**
 * The cover screen shown once before the prologue narration: full-bleed key
 * art with the game's logo up top. While `preloadUrls` (this art + the
 * prologue's own background, so neither pops in mid-scene) are still
 * loading, a 봉인석 spinner sits where the tap hint goes — its glow cycling
 * through colors, then settling to green once loaded — before tap (or
 * Enter/Space) fires `onStart`, which leads into the prologue's Subtitles
 * scene.
 */
export function TitleIntro({
  bg,
  preloadUrls,
  onStart,
}: {
  bg: string;
  preloadUrls: readonly string[];
  onStart: () => void;
}) {
  const gt = useGt();
  const t = useT();
  const [phase, setPhase] = useState<Phase>("loading");

  useEffect(() => {
    let loaded = 0;
    let cancelled = false;
    const total = preloadUrls.length || 1;
    const started = Date.now();

    const finish = () => {
      if (cancelled) return;
      setTimeout(() => {
        if (cancelled) return;
        setPhase("settled");
        setTimeout(() => {
          if (!cancelled) setPhase("ready");
        }, SETTLE_MS);
      }, Math.max(0, MIN_LOAD_MS - (Date.now() - started)));
    };

    if (preloadUrls.length === 0) {
      finish();
      return;
    }

    for (const url of preloadUrls) {
      const img = new Image();
      img.onload = img.onerror = () => {
        loaded += 1;
        if (loaded >= total) finish();
      };
      img.src = url;
    }

    return () => {
      cancelled = true;
    };
  }, [preloadUrls]);

  const ready = phase === "ready";

  useEffect(() => {
    if (!ready) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        onStart();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [ready, onStart]);

  return (
    <div
      className="cam-screen cam-title"
      style={{ ["--cam-bg" as string]: `url(${bg})` }}
      role="button"
      tabIndex={0}
      aria-label={t("play")}
      onClick={ready ? onStart : undefined}
    >
      <OptionsButton />

      <div className="cam-title-logo">
        <img
          className="cam-title-logo-img"
          src={TITLE_LOGO}
          alt={`${gameConfig.title} — ${gameConfig.subtitle}`}
        />
      </div>

      <div className={`cam-subtitle${ready ? "" : " no-scrim"}`}>
        {ready ? (
          <span className="cam-hint">{gt("tapStart")}</span>
        ) : (
          <div className={`cam-loading-spinner${phase === "settled" ? " is-settled" : ""}`}>
            <img className="cam-loading-stone" src={PACT_STONE} alt="" aria-hidden />
            <svg className="cam-loading-ring" viewBox="0 0 100 100" aria-hidden>
              <circle cx="50" cy="50" r="46" />
            </svg>
          </div>
        )}
      </div>
    </div>
  );
}
